import AppSettings from "@/appsettings";

// Timing instrumentation for the cold-login critical path. LOCAL AND DEV ONLY.
//
// This exists to produce a baseline to measure the web performance pass against
// (WorkRepos/TPlans/web-performance-and-login-cleanup-plan.md). That plan's
// impact estimates are derived from measured *bytes*, not observed timings —
// there is no RUM anywhere in the login flow — so the ordering of its work items
// rests on an assumption about whether JS parse time or round trips dominate.
// This answers that question with numbers before anyone spends effort on it.
//
// Why a sessionStorage ledger rather than performance.measure(). A cold login
// spans three documents: the app at "/", Microsoft's hosted login, and the app
// again at /auth/callback. performance marks are per-document and do not survive
// a cross-document navigation, so the two most expensive stretches — time spent
// at the identity provider, and the second full boot of the bundle on the way
// back — are invisible to the Performance API alone. Epoch timestamps in
// sessionStorage do survive it (same origin, same tab), which is the only way to
// see the journey end to end. performance.mark() is still called alongside, so
// the same boundaries show up in a DevTools trace.
//
// The ledger is an ordered append-only list rather than a fixed set of segments.
// Names repeat by design — DOCUMENT_START and REACT_MOUNTED appear once per
// document — and a retried identity bootstrap adds a second USER_DATA_REQUESTED.
// Reading consecutive deltas therefore shows the flow as it actually ran,
// including the parts that ran twice, which is precisely what the plan suspects
// is expensive.
//
// Nothing here may throw. It sits directly on the sign-in path, and a broken
// measurement must never be able to break a login.

const STORAGE_KEY = "engage.perf.loginTimeline";
const MARK_PREFIX = "engage.login:";
// A completed journey is ~10 entries. The cap only matters for a flow that never
// reaches DASHBOARD_READY — a redirect loop, say — where the ledger would
// otherwise grow for as long as the tab lives.
const MAX_ENTRIES = 64;
// Generous: a real cold login spends most of its wall clock at the identity
// provider, and a slow human on an MFA prompt is still one journey. This only
// has to be shorter than "went to lunch mid-login".
const STALE_LEDGER_MS = 5 * 60 * 1000;

export const LOGIN_MARKS = Object.freeze({
  // Navigation start of the current document. Its delta to BUNDLE_EVALUATED is
  // the cost of fetching and parsing the bundle before any Engage code runs —
  // the number that decides whether the CloudFront compression fix (item 1) is
  // as dominant as the plan claims.
  DOCUMENT_START: "document-start",
  BUNDLE_EVALUATED: "bundle-evaluated",
  REACT_MOUNTED: "react-mounted",
  // The auth state machine left LOADING: this boot now knows whether there is a
  // session. On a cold login that answer is "no", and everything spent reaching
  // it was spent to discover that.
  AUTH_DECISION: "auth-decision",
  SIGNIN_REDIRECT: "signin-redirect",
  // First mark recorded after the return from Microsoft. Its delta to
  // SIGNIN_REDIRECT covers the provider round trip *and* the second bundle boot;
  // the intervening DOCUMENT_START in the ledger separates the two.
  CALLBACK_MOUNTED: "callback-mounted",
  CODE_EXCHANGED: "code-exchanged",
  USER_DATA_REQUESTED: "user-data-requested",
  USER_DATA_RESOLVED: "user-data-resolved",
  // Terminal mark: homepage Dynamic Page content has resolved and committed.
  DASHBOARD_READY: "dashboard-ready",
});

// AppSettings.environment is "dev" for both the dev host and localhost, and
// "test"/"staging"/"prod" everywhere else — so this single comparison is exactly
// the local-and-dev gate, with no new setting to keep in sync. Read at call time
// rather than captured at module load so it stays mockable.
const isEnabled = () =>
  AppSettings.environment === "dev" && typeof performance !== "undefined";

const nowEpoch = () =>
  typeof performance.timeOrigin === "number"
    ? performance.timeOrigin + performance.now()
    : Date.now();

const readLedger = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Storage unavailable or holding something we did not write. Start over
    // rather than propagating — a lost measurement is not worth an exception.
    return [];
  }
};

const writeLedger = (entries) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Nothing to do. The marks still land on the performance timeline.
  }
};

export const resetLoginTimeline = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignored, as above.
  }
};

const toRows = (entries) => {
  const start = entries[0].t;
  return entries.map((entry, index) => ({
    step: entry.name,
    // Time since the previous step. This is the column to read: it attributes
    // the total to individual stages.
    ms: Math.round(entry.t - (index === 0 ? start : entries[index - 1].t)),
    elapsed: Math.round(entry.t - start),
  }));
};

// Prints the journey recorded so far. Safe to call at any point — it is wired to
// fire automatically on DASHBOARD_READY, and exposed on window for reading a
// flow that stalled before then.
export const reportLoginTimeline = () => {
  if (!isEnabled()) {
    return null;
  }

  const entries = readLedger();
  if (entries.length < 2) {
    return null;
  }

  const rows = toRows(entries);
  const total = rows[rows.length - 1].elapsed;

  try {
    console.info(
      `[perf] cold login timeline — ${total} ms across ${entries.length} steps`,
    );
    console.table(rows);
  } catch {
    // Ignored.
  }

  return rows;
};

// Reading a stalled or unusual flow from the console, without having to reach a
// terminal mark first. Installed on first use rather than at import time so that
// merely importing this module has no observable effect.
const installDevHandles = () => {
  if (globalThis.__engageLoginTimeline) {
    return;
  }
  globalThis.__engageLoginTimeline = reportLoginTimeline;
  globalThis.__engageLoginTimelineReset = resetLoginTimeline;
};

export const markLoginEvent = (name, { once = false } = {}) => {
  if (!isEnabled() || !name) {
    return;
  }

  try {
    performance.mark?.(`${MARK_PREFIX}${name}`);

    const entries = readLedger();
    if (entries.length >= MAX_ENTRIES) {
      return;
    }
    if (once && entries.some((entry) => entry.name === name)) {
      return;
    }

    entries.push({ name, t: nowEpoch() });
    writeLedger(entries);

    if (name === LOGIN_MARKS.DASHBOARD_READY) {
      reportLoginTimeline();
    }
  } catch {
    // Never break the login path over instrumentation.
  }
};

// Records the two marks every document boot contributes, and decides whether
// this boot begins a new journey.
//
// A ledger whose last entry is DASHBOARD_READY belongs to a login that already
// finished, so this boot starts a fresh one. Any other last entry means the
// journey is still in flight — the boot at /auth/callback lands here with
// SIGNIN_REDIRECT last — and must append to what is already there, or the
// provider round trip is lost.
export const markAppBoot = () => {
  if (!isEnabled()) {
    return;
  }

  try {
    installDevHandles();

    const entries = readLedger();
    const last = entries[entries.length - 1];
    // A finished journey is obvious. An abandoned one is not: a login the user
    // walked away from leaves a partial ledger that the next attempt appends to,
    // and the two are printed as one run — which is how a 587-second "login"
    // gets reported. Anything older than a plausible single journey is stale, so
    // start clean rather than silently concatenating two attempts.
    const isStale = last && nowEpoch() - last.t > STALE_LEDGER_MS;
    if (last && (last.name === LOGIN_MARKS.DASHBOARD_READY || isStale)) {
      resetLoginTimeline();
    }

    // DOCUMENT_START is backdated to this document's navigation start, so the
    // delta that follows is genuinely "bundle download + parse" and not just the
    // sliver of it after our first line of code ran.
    const entriesAfterReset = readLedger();
    if (entriesAfterReset.length < MAX_ENTRIES) {
      entriesAfterReset.push({
        name: LOGIN_MARKS.DOCUMENT_START,
        t: nowEpoch() - performance.now(),
      });
      writeLedger(entriesAfterReset);
    }

    markLoginEvent(LOGIN_MARKS.BUNDLE_EVALUATED);
  } catch {
    // Ignored, as above.
  }
};
